import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FaHome } from 'react-icons/fa';
import { selectLanguage } from '../redux/Slices/busSlice';
import logo from '../../components/assets/logos/logo2.PNG';

const AboutTeam = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentLanguage = useSelector(selectLanguage);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    
    htmlElement.lang = currentLanguage;
    htmlElement.classList.remove('hindi-lang');
    bodyElement.classList.remove('hindi-lang');
    
    if (currentLanguage === 'hi') {
      htmlElement.classList.add('hindi-lang');
      bodyElement.classList.add('hindi-lang');
    }
    
    htmlElement.classList.add('font-transition');
    bodyElement.classList.add('font-transition');
    
    return () => {
      htmlElement.classList.remove('hindi-lang', 'font-transition');
      bodyElement.classList.remove('hindi-lang', 'font-transition');
    };
  }, [currentLanguage]);

  const getFontConfig = () => {
    if (currentLanguage === 'hi') {
      return {
    fontFamily: "'Noto Serif Devanagari', serif",

        fontWeight: 500,
        className: 'hindi-lang font-hindi'
      };
    }
    return {
     fontFamily: "'Noto Serif Devanagari', serif",


      fontWeight: 400,
      className: 'font-english'
    };
  };

  const fontConfig = getFontConfig();

  // Team members data with translation keys
  const developers = [
    { 
      nameKey: 'harikaName', 
      nameDefault: 'Harika', 
      roleKey: 'frontendDeveloper', 
      roleDefault: 'Frontend Developer', 
      descKey: 'frontendDesc', 
      descDefault: 'Frontend Development & UI Implementation' 
    },
    { 
      nameKey: 'rameshName', 
      nameDefault: 'Ramesh Nediyadath', 
      roleKey: 'backendDeveloper', 
      roleDefault: 'Backend Developer', 
      descKey: 'backendDesc', 
      descDefault: 'Backend Development & API Integration' 
    },
  ];

  // Conditional text based on language
  const getPartnerName = (type) => {
    if (currentLanguage === 'hi') {
      if (type === 'ca') return 'नरेंद्र भण्डारी एण्ड कं.';
      if (type === 'tm') return 'अंशुल कंसल्टेंसी प्रा. लि.';
    }
    // English defaults
    if (type === 'ca') return 'Narendra Bhandari & Co.';
    if (type === 'tm') return 'Anshul Consultancy Pvt. Ltd.';
    return '';
  };

  const getConceptText = () => {
    if (currentLanguage === 'hi') return 'राजदूत एक्सप्लोरेशंस';
    return 'Rajdoot Explorations';
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      fontFamily: fontConfig.fontFamily,
      fontWeight: fontConfig.fontWeight,
      padding: isMobile ? '10px' : '0',
      overflowX: 'hidden',
      transition: 'font-family 0.3s ease, font-weight 0.3s ease'
    },
    header: {
      background: 'linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%)',
      color: 'black',
      padding: isMobile ? '10px 20px' : '10px 40px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      borderBottom: '3px solid #ffeb3b'
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    },
    logoLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      textDecoration: 'none',
      color: 'black'
    },
    logoImage: {
      height: isMobile ? '45px' : '60px',
      width: 'auto',
      objectFit: 'contain',
      borderRadius: '8px'
    },
    homeButton: {
      background: 'none',
      border: 'none',
      color: 'black',
      fontSize: isMobile ? '1rem' : '1.1rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 15px',
      borderRadius: '20px',
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      transition: 'all 0.3s ease',
      textDecoration: 'none'
    },
    mainContent: {
      flex: '1',
      padding: isMobile ? '20px' : '40px',
      maxWidth: '1000px',
      margin: '0 auto',
      width: '100%',
      boxSizing: 'border-box'
    },
    pageCard: {
      background: 'white',
      borderRadius: '20px',
      padding: isMobile ? '25px' : '40px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      marginBottom: '30px'
    },
    pageTitle: {
      color: '#333',
      fontSize: isMobile ? '2rem' : '2.5rem',
      marginBottom: '30px',
      textAlign: 'center',
      fontWeight: 700,
      borderBottom: '3px solid #ffeb3b',
      paddingBottom: '15px'
    },
    section: {
      marginBottom: '40px'
    },
    sectionTitle: {
      color: '#444',
      fontSize: isMobile ? '1.5rem' : '1.8rem',
      marginBottom: '20px',
      fontWeight: 600,
      textAlign: 'center'
    },
    implementingAgency: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '30px',
      borderRadius: '15px',
      textAlign: 'center',
      marginBottom: '30px'
    },
    agencyName: {
      fontSize: isMobile ? '1.8rem' : '2.2rem',
      fontWeight: 700,
      marginBottom: '10px'
    },
    agencyRole: {
      fontSize: isMobile ? '1.1rem' : '1.3rem',
      opacity: '0.9'
    },
    teamGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '20px',
      marginBottom: '30px'
    },
    teamCard: {
      background: '#f8f9fa',
      borderRadius: '12px',
      padding: '20px',
      textAlign: 'center',
      transition: 'all 0.3s ease',
      border: '2px solid transparent'
    },
    teamName: {
      color: '#333',
      fontSize: '1.2rem',
      fontWeight: 600,
      marginBottom: '5px'
    },
    teamRole: {
      color: '#667eea',
      fontSize: '0.95rem',
      marginBottom: '10px',
      fontWeight: 500
    },
    teamDescription: {
      color: '#666',
      fontSize: '0.9rem',
      lineHeight: '1.5'
    },
    partnersSection: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '20px',
      marginTop: '30px'
    },
    partnerCard: {
      background: '#fff9c4',
      padding: '25px',
      borderRadius: '12px',
      textAlign: 'center'
    },
    partnerTitle: {
      color: '#333',
      fontSize: '1.2rem',
      fontWeight: 600,
      marginBottom: '10px'
    },
    partnerName: {
      color: '#555',
      fontSize: '1.1rem',
      marginBottom: '5px'
    },
    footer: {
      background: '#fff9c4',
      color: 'black',
      padding: isMobile ? '15px 20px' : '25px 40px',
      textAlign: 'center',
      marginTop: '40px',
      width: '100%',
      boxSizing: 'border-box'
    },
    footerText: {
      margin: '0 0 10px 0',
      opacity: '0.9',
      fontSize: isMobile ? '0.8rem' : '0.95rem'
    },
    footerLinks: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: isMobile ? '15px' : '25px'
    },
    footerLink: {
      color: 'black',
      textDecoration: 'none',
      opacity: '0.8',
      transition: 'opacity 0.3s ease',
      fontSize: isMobile ? '0.8rem' : '0.9rem'
    }
  };

  return (
    <div style={styles.container} className={fontConfig.className} lang={currentLanguage}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logoContainer}>
            <Link to="/" style={styles.logoLink}>
              <img src={logo} alt="Logo" style={styles.logoImage} />
            </Link>
          </div>
          <Link 
            to="/" 
            style={styles.homeButton}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.5)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
          >
            <FaHome /> {t('home', 'Home')}
          </Link>
        </div>
      </header>

      <main style={styles.mainContent}>
        <div style={styles.pageCard}>
          <h1 style={styles.pageTitle}>
            {t('aboutTeam', 'About Our Team')}
          </h1>

          {/* Implementing Agency */}
          <div style={styles.implementingAgency}>
            <div style={styles.agencyName}>
              {currentLanguage === 'hi' ? 'नेटसृष्टि' : 'Netsrishti'}
            </div>
            <div style={styles.agencyRole}>
              {t('implementingAgency', 'Implementing Agency for the Project')}
            </div>
          </div>

          {/* Development Team */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              {t('developmentTeam', 'Development Team')}
            </h2>
            <div style={styles.teamGrid}>
              {developers.map((dev, index) => (
                <div 
                  key={index}
                  style={styles.teamCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = '#667eea';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <div style={styles.teamName}>{t(dev.nameKey, dev.nameDefault)}</div>
                  <div style={styles.teamRole}>{t(dev.roleKey, dev.roleDefault)}</div>
                  <div style={styles.teamDescription}>{t(dev.descKey, dev.descDefault)}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{
            marginTop: '40px',
            padding: '20px',
            background: '#fff9c4',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.2rem', color: '#333', fontWeight: 600, marginBottom: '10px' }}>
              {t('mvpVersion', 'Current MVP Version')}
            </div>
            <div style={{ color: '#666' }}>
              {t('mvpDescription', 'Minimum Viable Product - Developed and maintained by Netsrishti')}
            </div>
          </div>
          {/* Partners */}
          <div style={styles.partnersSection}>
            <div style={styles.partnerCard}>
              <div style={styles.partnerTitle}>
                {t('charteredAccountants', 'Chartered Accountants')}
              </div>
              <div style={styles.partnerName}>{getPartnerName('ca')}</div>
            </div>

            <div style={styles.partnerCard}>
              <div style={styles.partnerTitle}>
                {t('trademarkConsultants', 'Trademark Consultants')}
              </div>
              <div style={styles.partnerName}>{getPartnerName('tm')}</div>
            </div>
          </div>

          {/* Concept */}
          <div style={{ 
            marginTop: '30px', 
            padding: '20px', 
            background: '#f5f5f5', 
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.2rem', color: '#333', fontWeight: 600 }}>
              {t('concept', 'Concept')}
            </div>
            <div style={{ fontSize: '1.5rem', color: '#764ba2', marginTop: '10px' }}>
              {getConceptText()}
            </div>
          </div>

         
        </div>
      </main>

      <footer style={styles.footer}>
        <p style={styles.footerText}>
          © {new Date().getFullYear()} {t('companyName', 'Rajdoot Explorations')}. {t('allRightsReserved', 'All rights reserved.')}
        </p>
        <div style={styles.footerLinks}>
          <Link to="/privacy" style={styles.footerLink}>{t('privacyPolicy', 'Privacy Policy')}</Link>
          <Link to="/terms" style={styles.footerLink}>{t('termsOfService', 'Terms of Service')}</Link>
          <Link to="/contact" style={styles.footerLink}>{t('contactUs', 'Contact Us')}</Link>
        </div>
      </footer>
    </div>
  );
};

export default AboutTeam;